import { useState } from 'react';
import { Dialog, Box, Typography, IconButton, Button, Stack, Chip, ArrowBackIcon, CloseIcon, 
    LocationOnIcon, UserFlag, StarRateIcon, StarOutlineIcon } from 'src/components';
import { useIsMobile, useNetworkStatus } from 'src/hooks';
import { useAuth } from 'src/utils';
import { useFeatureTravellerStore } from 'src/stores';
import { getCategorySymbol } from 'src/data';
import { ALL_COUNTRIES } from 'src/data';

import EditFeatureDetailsModal from './edit-feature-details-modal';
import AddFeatureModal from './add-feature-modal';

import placeholderImage from 'src/assets/placeholders/light-grey-1080x1480.png';

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default function FeatureDetailsModal({ onClose, handleFavouriteClick, isFavourite, favouriteRequestLoading }) {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const selectedFeature = useFeatureTravellerStore((state) => state.selectedFeature);

    const online = useNetworkStatus();

    if (!selectedFeature) return null;

    const countryIsoAlpha2 = ALL_COUNTRIES.find(x => x.name === selectedFeature.country)?.code;

    const {
        title,
        description,
        googleMapsUrl,
        country,
        category,
        images,
    } = selectedFeature;

    const hasImages = Array.isArray(images) && images.length > 0;
    const categoryIcon = getCategorySymbol(category);

    const slides = hasImages && online 
        ? images 
        : [placeholderImage];

    return (<>
        <Dialog
            open
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    overflow: 'hidden',
                },
            }}
        >
            <Box className="scrollBox" sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', overflowY: 'auto' }}>
                
                {/* Hero Carousel Section */}
                <Box sx={{ flexShrink: 0, overflow: 'hidden' }}>
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        navigation={!isMobile}
                        pagination={{ clickable: true }}
                        autoplay={{
                            delay: 6000,
                            disableOnInteraction: true,
                            pauseOnMouseEnter: true,
                        }}
                        loop={slides.length > 1}
                        watchOverflow={true}
                        spaceBetween={0}
                        slidesPerView={1}
                        style={{
                            width: '100%',
                            height: '60vh',
                            '--swiper-pagination-color': '#fff',
                            '--swiper-pagination-bullet-inactive-color': '#fff',
                            '--swiper-pagination-bullet-inactive-opacity': '0.5',
                            '--swiper-pagination-bullet-size': '8px',
                            '--swiper-pagination-bottom': '50px', 
                            '--swiper-navigation-color': '#fff',
                            '--swiper-navigation-size': '30px',
                        }}
                    >
                        {slides.map((imgSrc, index) => (
                            <SwiperSlide key={index}>
                                <Box
                                    component="img"
                                    src={imgSrc}
                                    onError={(e) => {
                                        e.currentTarget.src = placeholderImage;
                                    }}
                                    alt={`${title} - ${index + 1}`}
                                    sx={{
                                        width: '100%',
                                        height: '60vh',
                                        objectFit: 'cover',
                                    }}
                                    loading="lazy"
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>

                {/* Close / Back Button */}
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: isMobile ? 'calc(12px + var(--safe-area-top))' : 12,
                        left: isMobile ? 12 : 'auto',
                        right: isMobile ? 'auto' : 12,
                        zIndex: 10,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                    }}
                >
                    {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
                </IconButton>

                {/* White content box with top curves and overlap */}
                <Box sx={{ 
                    flex: 1, 
                    p: 4, 
                    mt: -5, 
                    bgcolor: '#fff', 
                    borderTopRightRadius: 30, 
                    borderTopLeftRadius: 30,
                    position: 'relative',
                    zIndex: 1,
                }}>
                    <Box sx={{mb: 1, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="h4" fontWeight="bold">
                            {title}
                        </Typography>
                        
                        <IconButton 
                            size="small" 
                            loading={favouriteRequestLoading}
                            onClick={() => handleFavouriteClick(selectedFeature.id)}>
                            {isFavourite ? 
                                <StarRateIcon fontSize="small" /> 
                            : <StarOutlineIcon fontSize="small" />}
                        </IconButton>
                    </Box>

                    {googleMapsUrl && (
                        <Button
                            href={googleMapsUrl}
                            target="_blank"
                            startIcon={<LocationOnIcon fontSize="small" sx={{ mb: 0.2 }} />}
                            size="small"
                            sx={{ mb: 1, ml: -0.8 }}
                        >
                            Open in Google Maps
                        </Button>
                    )}

                    <Typography variant={isMobile ? "body1" : "body2"} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                        {description}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                        <Chip
                            icon={<UserFlag countryCode={countryIsoAlpha2} size={18} sx={{ml: 1}} />}
                            label={country}
                        />
                        {category && (
                            <Chip
                                icon={
                                    categoryIcon ? (
                                        <Box
                                        component="img"
                                        src={categoryIcon}
                                        alt={category}
                                        sx={{ width: 20, height: 20 }}
                                        />
                                    ) : null
                                }
                                label={category}
                            />
                        )}
                    </Stack>

                    {user?.role === 'Admin' && (
                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button variant="outlined" color="primary" onClick={() => setEditModalOpen(true)}>
                                Edit
                            </Button>
                            <Button variant="outlined" color="primary" onClick={() => setAddModalOpen(true)}>
                                Create New Map Feature
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Box>
        </Dialog>

        {editModalOpen && (
            <EditFeatureDetailsModal 
                onClose={() => setEditModalOpen(false)} />
        )}

        {addModalOpen && (
            <AddFeatureModal 
                onClose={() => setAddModalOpen(false)} />
        )}
    </>);
}